import { Alert, Button, TextField } from '@setel/portal-ui';
import * as React from 'react';
import { useMutation } from 'react-query';
// import { useHistory, useLocation } from 'react-router-dom';
import { I2FALoginInput, login, login2FA } from 'services/api-ops.service';
import { IamNamespaces, ILoginResponse } from 'shared/consts/auth.const';
import { LoginStateEnum, useAuth } from './auth.context';
import {
  getSessionData,
  saveSession,
  setAccessTokenInHeader,
} from './auth.service';
import jwtDecode from 'jwt-decode';
import { AxiosError } from 'axios';
import * as GA from 'shared/analytics/gtag';
import { useLocation } from 'react-router';

export function LoginForm() {
  // added ga for url tracking
  let location = useLocation();
  React.useEffect(() => {
    GA.pageview(location.pathname, 'Login');
  }, [location]);

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  const [step, setStep] = React.useState<'credential' | 'otp'>('credential');
  const [otp, setOtp] = React.useState('');
  const otpInputRef = React.useRef<HTMLInputElement>(null);
  const { setSessionData, setSessionPayload, setLoginState } = useAuth();

  const {
    mutate: invokeLogin,
    isLoading,
    error: loginError,
    data: loginResult,
    isError: isLoginError,
  } = useMutation(
    ({
      email,
      password,
      namespace,
    }: {
      email: string;
      password: string;
      namespace: IamNamespaces;
    }) => login(email, password, namespace),
    {
      onSuccess: (res) => {
        //add GA for event tracking

        if (res?.authenticityToken) {
          return setStep('otp');
        }

        const loginResponse = res as ILoginResponse;

        loginAction(loginResponse);
      },
    }
  );
  // update loginState and localStorage
  const loginAction = (res: ILoginResponse) => {
    GA.event({
      action: 'agent_login_form',
      label: 'Agent Logged In',
      category: 'web-zendesk',
      value: '',
    });
    const expiresAt = new Date();
    expiresAt.setSeconds(res.expiresIn + expiresAt.getSeconds());

    const session = {
      accessToken: res.accessToken,
      email,
      refreshToken: res.refreshToken,
      expiresAt: expiresAt.toISOString(),
      username: res.username,
    };
    /* set local storage */
    saveSession(session);

    /* set context */
    getSessionData();
    setSessionPayload(session);
    setSessionData(jwtDecode(res.accessToken));
    setAccessTokenInHeader();
    setLoginState(LoginStateEnum.LOGGED_IN);
  };

  const {
    mutate: invokeLogin2FA,
    isLoading: isSubmitting2FA,
    error: login2FAError,
    isError: is2FALoginError,
  } = useMutation((input: I2FALoginInput) => login2FA(input), {
    onSuccess: (res) => {
      loginAction(res);
    },
    onError: () => {
      setOtp('');
      if (otpInputRef.current) {
        otpInputRef.current.focus();
      }
    },
  });

  const error = login2FAError || loginError;

  const serverError =
    error === 'jwt expired'
      ? 'Incorrect email or password'
      : (error as AxiosError<{ message: string }>)?.response?.data?.message;

  return (
    <>
      <div className="bg-white shadow-lg max-w-lg mx-auto pt-12 pb-8 px-6 sm:px-10">
        {(isLoginError || is2FALoginError) && (
          <Alert variant="error" description={serverError} />
        )}
        {step === 'credential' && (
          <>
            <form
              onSubmit={(ev) => {
                ev.preventDefault();
                invokeLogin({
                  email,
                  password,
                  namespace: IamNamespaces.SETEL_ADMINS,
                });
              }}
              autoComplete="off"
              className="my-4"
            >
              {/* adding a hidden password field make some browsers to disable password autofill */}
              <input type="email" tabIndex={-1} className="sr-only" />
              <input type="password" tabIndex={-1} className="sr-only" />
              <TextField
                label="Email address"
                value={email}
                type="email"
                onChangeValue={setEmail}
                required
                disabled={isLoading}
                wrapperClass="mb-6"
                autoComplete="off"
              />
              <TextField
                label="Password"
                value={password}
                onChangeValue={setPassword}
                type="password"
                disabled={isLoading}
                minLength={8}
                wrapperClass="mb-6"
                required
                autoComplete="off"
              />
              <div>
                <Button
                  variant="primary"
                  type="submit"
                  className="w-full"
                  isLoading={isLoading}
                >
                  LOGIN
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 'otp' && (
          <form
            onSubmit={(ev) => {
              ev.preventDefault();
              invokeLogin2FA({
                authenticityToken:
                  (loginResult && loginResult.authenticityToken) || '',
                otp,
                identifier: email,
              });
            }}
            className="my-4"
          >
            <TextField
              label="Verification code"
              value={otp}
              onChangeValue={setOtp}
              autoFocus
              ref={otpInputRef}
            />
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              isLoading={isSubmitting2FA}
            >
              VERIFY
            </Button>
          </form>
        )}
      </div>
    </>
  );
}
